<script lang="ts">
  import { max, sumBy, round, uniq, times, orderBy } from 'lodash-es'
  import { scaleLinear, scaleOrdinal } from 'd3'

  type Datum = { value: number; label: string; group?: Datum[] }

  export let dataset: Datum[] = []
  export let stacked = false
  export let limit = null

  function addPercentages(dataset: Datum[], column: string): (Datum & { percentage: number })[] {
    const sum = sumBy(dataset, column)
    return dataset.map((d) => ({ ...d, percentage: (d[column] / sum) * 100 }))
  }

  $: datasetWithPercentage = orderBy(
    addPercentages(dataset, 'value').map((item) => ({
      ...item,
      group: item.group ? addPercentages(item.group, 'value') : undefined,
    })),
    'percentage',
    'desc'
  ).slice(0, limit !== null ? limit : dataset.length)

  $: maxPercentage = max(datasetWithPercentage.map((d) => d.percentage)) || 100
  $: barLenghtScale = scaleLinear([0, maxPercentage], [0, 100])

  $: colorLabels = stacked
    ? uniq(datasetWithPercentage.flatMap((d) => d.group).map((g) => g.label))
    : []
  $: colorScale = !stacked
    ? () => '#00fff0'
    : colorLabels.includes('Femmina')
    ? scaleOrdinal()
        .range(['#FF8ED9', '#55D5FE', '#9365F4'])
        .domain(['Femmina', 'Maschio', 'Non dichiarato'])
    : scaleOrdinal()
        .range(
          colorLabels.length <= 6
            ? ['#00FFF0', '#00A99F', '#006963', '#FFFFFF', '#9E9E9E', '#686868']
            : times(colorLabels.length, (i) => {
                return scaleLinear()
                  .domain([0, colorLabels.length - 1])
                  .range(['#FFFFFF', '#006963'])(i)
              })
        )
        .domain(colorLabels)
</script>

<div>
  <div class="chart">
    {#if stacked && colorScale.domain().length > 0}
      <div class="legend">
        {#each colorScale.domain() as tick}
          <div class="legend-element">
            <div class="square" style="background: {colorScale(tick)}" />
            <div class="label">{tick}</div>
          </div>
        {/each}
      </div>
    {/if}

    {#each datasetWithPercentage as { value, label, percentage, group }}
      <div class="row">
        <div class="labels-container">
          <div class="label center-vertical">{label}</div>
          <div class="value center-vertical">{value.toLocaleString('en-US')}</div>
          <div class="percentage">{`${round(percentage)}%`}</div>
        </div>
        {#if group}
          <div class="bar" style="width: {barLenghtScale(percentage)}%;">
            {#each group as g}
              <div
                class="bar"
                style="width: {g.percentage}%; float: left; background: {colorScale(g.label)}"
              />
            {/each}
          </div>
        {:else}
          <div class="bar" style="width: {barLenghtScale(percentage)}%;" />
        {/if}
      </div>
    {:else}
      <div style="color: grey">Dati assenti</div>
    {/each}
  </div>
</div>

<style>
  .chart {
    margin-top: 20px;
  }

  .row {
    margin-bottom: 20px;
  }

  .labels-container {
    display: flex;
    justify-content: space-between;
    align-content: center;
    margin-bottom: 5px;
    font-size: 12px;
  }

  .center-vertical {
    display: flex;
    align-items: flex-end;
  }

  .label {
    flex: 1;
  }

  .percentage {
    text-align: right;
    width: 50px;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: -2px;
  }
  .bar {
    height: 10px;
    background: #00fff0;
  }

  .legend {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    font-size: 12px;
    margin-bottom: 20px;
  }

  .legend-element {
    display: flex;
    flex-direction: row;
    margin-right: 20px;
  }

  .square {
    width: 12px;
    height: 12px;
    margin: 3px;
    margin-right: 6px;
  }
</style>
